/* /admin/profile — single-screen user profile for the logged-in admin.

   Edits the user document via `PATCH /api/auth/me/preferences` (the same
   endpoint the top-bar dark/light toggle uses). Today's fields:

   • Display name        — text input, used in the welcome line + avatar initials
   • Email               — read-only (changing this would re-key the JWT + reset
                           tokens; we'll add a guarded change-email flow later)
   • Avatar URL          — text input, optional. The header avatar renders a
                           photo when set; falls back to initials otherwise.
   • Booking alerts      — single toggle (email me when a new booking arrives).
                           More notification opt-ins land here as we wire them.
   • Admin chrome theme  — dark/light radio. Mirrors the top-bar toggle so the
                           preference lives in one canonical place.

   The page hydrates from `useAuth().user`, posts a partial patch on save,
   and re-seeds the auth context with the refreshed user record so the rest
   of the admin (header initials, welcome line, chrome toggle) reflects the
   change instantly. */

import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Save, Sun, Moon, Upload, X } from "lucide-react";
import AvatarCropDialog from "@/components/admin/AvatarCropDialog";

function initialsFor(name, email) {
  const src = (name || email || "?").trim();
  const parts = src.split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return src.slice(0, 2).toUpperCase();
}

export default function AdminProfile() {
  const { user, setSession } = useAuth();

  // Local form state — initialised from the auth context the first time
  // `user` resolves. Kept in sync if the user object updates underneath us
  // (e.g. another tab edits the profile and the storage event fires).
  const [name, setName] = useState("");
  const [avatarUrl, setAvatarUrl] = useState("");
  const [notifyOnBooking, setNotifyOnBooking] = useState(true);
  const [chromeTheme, setChromeTheme] = useState("dark");
  const [saving, setSaving] = useState(false);
  // Change-email mini-flow state. Kept in this parent so the modal can read
  // the current user.email + setSession callback without prop-drilling.
  const [showChangeEmail, setShowChangeEmail] = useState(false);

  useEffect(() => {
    if (!user) return;
    setName(user.name || "");
    setAvatarUrl(user.avatar_url || "");
    setNotifyOnBooking(user.notify_on_booking !== false);  // default true
    setChromeTheme(user.chrome_theme === "light" ? "light" : "dark");
  }, [user?.id, user?.name, user?.avatar_url, user?.notify_on_booking, user?.chrome_theme]);

  useEffect(() => {
    document.title = "Your profile — Studio Manager";
  }, []);

  const initials = useMemo(() => initialsFor(name, user?.email), [name, user?.email]);

  // Dirty check — only enable Save when something actually changed.
  const dirty = useMemo(() => {
    if (!user) return false;
    return (
      name !== (user.name || "") ||
      avatarUrl !== (user.avatar_url || "") ||
      notifyOnBooking !== (user.notify_on_booking !== false) ||
      chromeTheme !== (user.chrome_theme === "light" ? "light" : "dark")
    );
  }, [user, name, avatarUrl, notifyOnBooking, chromeTheme]);

  // Avatar upload — drag/drop or click to pick opens a crop+zoom dialog;
  // confirming there hands us a square JPEG Blob which we POST to the
  // existing `/api/admin/uploads` endpoint (same one hero / portfolio
  // images use). The returned `/api/files/{id}` URL is what we store on
  // the user document. We intentionally don't auto-save after upload so
  // the "Save changes" button stays the single canonical commit point —
  // but the preview avatar updates instantly so it feels like a direct
  // interaction.
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  // The file the user just picked, waiting in the crop dialog. `null`
  // means the dialog is closed.
  const [pendingFile, setPendingFile] = useState(null);

  function pickFile(file) {
    if (!file) return;
    if (!file.type?.startsWith("image/")) {
      toast.error("Please drop an image (JPG, PNG, WEBP, GIF, or HEIC).");
      return;
    }
    if (file.size > 15 * 1024 * 1024) {
      toast.error("Avatar too large — please use an image under 15 MB.");
      return;
    }
    setPendingFile(file);
  }

  async function uploadCroppedBlob(blob, filename) {
    if (!blob) return;
    setUploading(true);
    setPendingFile(null);
    try {
      const fd = new FormData();
      // Wrap the Blob as a File so the backend can read the extension.
      fd.append("file", new File([blob], filename || "avatar.jpg", { type: blob.type || "image/jpeg" }));
      const { data } = await api.post("/admin/uploads", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (data?.url) {
        setAvatarUrl(data.url);
        toast.success("Avatar ready — click Save changes to keep it.");
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err) || "Couldn't upload — please try a smaller image.");
    } finally {
      setUploading(false);
    }
  }

  function onDropAvatar(e) {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer?.files?.[0];
    pickFile(f);
  }

  async function onSave() {
    if (!dirty || saving) return;
    const trimmed = name.trim();
    if (!trimmed) {
      toast.error("Display name can't be empty.");
      return;
    }
    setSaving(true);
    try {
      const { data: refreshed } = await api.patch("/auth/me/preferences", {
        name: trimmed,
        avatar_url: avatarUrl.trim(),
        notify_on_booking: notifyOnBooking,
        chrome_theme: chromeTheme,
      });
      // Re-seed the auth context so the header initials, welcome line, and
      // chrome toggle instantly reflect the new values — no page reload.
      const token = localStorage.getItem("allure_token");
      if (token && refreshed) setSession(token, refreshed);
      // Broadcast the chrome change so AdminLayout's sync effect picks it up
      // even though we're inside the auth context already (defensive).
      window.dispatchEvent(new CustomEvent("lumetier:admin-chrome-changed", {
        detail: { dark: chromeTheme === "dark" },
      }));
      toast.success("Profile updated.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err) || "Couldn't save — please try again.");
    } finally {
      setSaving(false);
    }
  }

  if (!user) {
    return (
      <div className="px-6 lg:px-12 pt-10" data-testid="admin-profile-loading">
        <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-6 lg:px-12 pt-10 pb-16" data-testid="admin-profile">
      {/* Header — back link + page title */}
      <Link
        to="/admin"
        className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.32em] text-muted-foreground hover:text-foreground transition-colors"
        data-testid="profile-back-link"
      >
        <ArrowLeft className="w-3 h-3" /> Studio Manager
      </Link>
      <div className="text-[10px] uppercase tracking-[0.32em] text-[hsl(var(--gold))] mt-7">
        Account
      </div>
      <h1 className="text-4xl sm:text-5xl font-serif font-light mt-3 leading-tight tracking-tight">
        Your profile.
      </h1>
      <p className="text-sm text-muted-foreground mt-3 leading-relaxed max-w-xl">
        The way you show up across the Studio Manager — and how we reach you
        when a new booking lands. Changes are stored against your account, so
        they follow you to every device you sign in from.
      </p>

      {/* Identity card */}
      <section className="mt-12 border border-border rounded-sm bg-card p-7" data-testid="profile-identity-section">
        <div className="flex items-center gap-5">
          {avatarUrl ? (
            <img
              src={avatarUrl}
              alt={name || "Avatar"}
              className="w-16 h-16 rounded-full object-cover ring-2 ring-[hsl(var(--gold))] ring-offset-2 ring-offset-card"
              data-testid="profile-avatar-preview"
              onError={(e) => { e.currentTarget.src = ""; }}
            />
          ) : (
            <div
              className="w-16 h-16 rounded-full bg-[hsl(var(--gold))]/15 ring-2 ring-[hsl(var(--gold))] ring-offset-2 ring-offset-card flex items-center justify-center text-[hsl(var(--gold))] text-base font-semibold"
              data-testid="profile-avatar-initials"
            >
              {initials}
            </div>
          )}
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-[0.28em] text-muted-foreground">Signed in as</div>
            <div className="text-base font-medium text-foreground truncate mt-1">{user.email}</div>
            <div className="text-[10px] uppercase tracking-[0.32em] text-[hsl(var(--gold))] mt-1.5">
              {user.role || "Admin"}
            </div>
          </div>
        </div>

        <div className="mt-7 grid sm:grid-cols-2 gap-5">
          <label className="block">
            <span className="block text-[10px] uppercase tracking-[0.32em] text-muted-foreground mb-2">
              Display name
            </span>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={80}
              placeholder="e.g. Bisi"
              className="w-full h-10 px-3 bg-background border border-border rounded-sm text-sm text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:border-[hsl(var(--gold))] transition-colors"
              data-testid="profile-name-input"
            />
            <span className="block text-[11px] text-muted-foreground/70 mt-1.5">
              Used in the dashboard welcome line and your initials.
            </span>
          </label>
          <label className="block">
            <span className="block text-[10px] uppercase tracking-[0.32em] text-muted-foreground mb-2">
              Email
            </span>
            <button
              type="button"
              onClick={() => setShowChangeEmail(true)}
              data-testid="profile-change-email-trigger"
              className="w-full h-10 px-3 bg-background border border-border rounded-sm text-sm text-foreground text-left hover:border-[hsl(var(--gold))] transition-colors flex items-center justify-between group"
            >
              <span className="truncate">{user.email}</span>
              <span className="text-[10px] uppercase tracking-[0.32em] text-muted-foreground group-hover:text-[hsl(var(--gold))] transition-colors shrink-0 ml-3">
                change
              </span>
            </button>
            <span className="block text-[11px] text-muted-foreground/70 mt-1.5">
              Used for sign-in and password resets. Click to change.
            </span>
          </label>
        </div>

        <div className="mt-8">
          <div className="text-[10px] uppercase tracking-[0.32em] text-muted-foreground mb-3">
            Profile photo
          </div>
          {/* Drag-and-drop avatar zone. Three interaction surfaces:
              • drop a file onto the zone
              • click the zone (or "Choose image") to open the file picker
              • paste a URL into the bottom input as before (great for using
                an existing CDN/Gravatar/Unsplash link without re-uploading)  */}
          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDropAvatar}
            onClick={() => !uploading && fileInputRef.current?.click()}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                fileInputRef.current?.click();
              }
            }}
            className={`relative flex items-center gap-5 p-5 border rounded-sm cursor-pointer transition-colors ${
              dragOver
                ? "border-[hsl(var(--gold))] bg-[hsl(var(--gold))]/8"
                : "border-dashed border-border bg-background hover:border-[hsl(var(--gold))]/40 hover:bg-muted/30"
            }`}
            data-testid="profile-avatar-dropzone"
          >
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="sr-only"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) pickFile(f);
                e.target.value = "";
              }}
              data-testid="profile-avatar-file-input"
            />
            {/* Big preview tile */}
            <div className="relative shrink-0">
              {avatarUrl ? (
                <img
                  src={avatarUrl}
                  alt="Avatar preview"
                  className="w-20 h-20 rounded-full object-cover ring-2 ring-[hsl(var(--gold))] ring-offset-2 ring-offset-background"
                  data-testid="profile-avatar-zone-preview"
                  onError={(e) => { e.currentTarget.style.opacity = "0.3"; }}
                />
              ) : (
                <div className="w-20 h-20 rounded-full bg-[hsl(var(--gold))]/15 ring-2 ring-[hsl(var(--gold))] ring-offset-2 ring-offset-background flex items-center justify-center text-[hsl(var(--gold))] text-xl font-semibold">
                  {initials}
                </div>
              )}
              {uploading ? (
                <div className="absolute inset-0 rounded-full bg-background/70 flex items-center justify-center">
                  <Loader2 className="w-5 h-5 animate-spin text-[hsl(var(--gold))]" />
                </div>
              ) : null}
            </div>
            {/* Copy + action */}
            <div className="flex-1 min-w-0">
              <div className="text-sm text-foreground font-medium">
                {uploading ? "Uploading…" : dragOver ? "Drop to upload" : "Drag an image here, or click to choose"}
              </div>
              <div className="text-[11px] text-muted-foreground mt-1">
                JPG, PNG, WEBP, GIF, or HEIC · up to 15 MB.
              </div>
              <div className="mt-3 flex items-center gap-2 flex-wrap">
                <span
                  className="inline-flex items-center gap-1.5 px-3 h-8 text-[10px] uppercase tracking-[0.28em] border border-border bg-background text-foreground"
                  data-testid="profile-avatar-choose-btn"
                >
                  <Upload className="w-3 h-3" /> Choose image
                </span>
                {avatarUrl ? (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setAvatarUrl(""); }}
                    className="inline-flex items-center gap-1.5 px-3 h-8 text-[10px] uppercase tracking-[0.28em] text-muted-foreground hover:text-destructive transition-colors"
                    data-testid="profile-avatar-remove-btn"
                  >
                    <X className="w-3 h-3" /> Remove
                  </button>
                ) : null}
              </div>
            </div>
          </div>
          {/* URL fallback — power-user shortcut */}
          <label className="block mt-3">
            <span className="block text-[10px] uppercase tracking-[0.32em] text-muted-foreground/70 mb-2">
              Or paste a URL
            </span>
            <input
              type="url"
              value={avatarUrl}
              onChange={(e) => setAvatarUrl(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              placeholder="https://…"
              className="w-full h-9 px-3 bg-background border border-border rounded-sm text-xs text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:border-[hsl(var(--gold))] transition-colors"
              data-testid="profile-avatar-input"
            />
          </label>
        </div>
      </section>

      {/* Notifications card */}
      <section className="mt-7 border border-border rounded-sm bg-card p-7" data-testid="profile-notifications-section">
        <div className="text-[10px] uppercase tracking-[0.32em] text-[hsl(var(--gold))]">
          Notifications
        </div>
        <h2 className="text-xl font-serif mt-1">Email me about</h2>
        <button
          type="button"
          onClick={() => setNotifyOnBooking((v) => !v)}
          className="mt-5 w-full flex items-center justify-between gap-4 p-4 border border-border rounded-sm bg-background hover:bg-muted/40 transition-colors text-left"
          data-testid="profile-notify-booking-toggle"
          aria-pressed={notifyOnBooking}
        >
          <div>
            <div className="text-sm font-medium text-foreground">New bookings</div>
            <div className="text-[11px] text-muted-foreground mt-0.5">
              We email you the moment a client reserves a session.
            </div>
          </div>
          <span
            className={`relative inline-block w-10 h-5 rounded-full transition-colors ${
              notifyOnBooking ? "bg-[hsl(var(--gold))]" : "bg-muted-foreground/30"
            }`}
          >
            <span
              className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-background transition-transform ${
                notifyOnBooking ? "translate-x-5" : "translate-x-0"
              }`}
            />
          </span>
        </button>
      </section>

      {/* Appearance card */}
      <section className="mt-7 border border-border rounded-sm bg-card p-7" data-testid="profile-appearance-section">
        <div className="text-[10px] uppercase tracking-[0.32em] text-[hsl(var(--gold))]">
          Appearance
        </div>
        <h2 className="text-xl font-serif mt-1">Studio Manager theme</h2>
        <p className="text-[11px] text-muted-foreground mt-1">
          Follows you to every device. The top-bar pill stays in sync.
        </p>
        <div className="mt-5 grid sm:grid-cols-2 gap-3">
          {[
            { id: "dark",  label: "Dark",  caption: "Editorial obsidian — the new default.", icon: Moon },
            { id: "light", label: "Light", caption: "Soft cream surface.",                   icon: Sun  },
          ].map((opt) => {
            const Icon = opt.icon;
            const active = chromeTheme === opt.id;
            return (
              <button
                key={opt.id}
                type="button"
                onClick={() => setChromeTheme(opt.id)}
                className={`flex items-center gap-3 p-4 border rounded-sm text-left transition-colors ${
                  active
                    ? "border-[hsl(var(--gold))] bg-[hsl(var(--gold))]/5"
                    : "border-border bg-background hover:bg-muted/40"
                }`}
                data-testid={`profile-chrome-${opt.id}`}
                aria-pressed={active}
              >
                <Icon className={`w-4 h-4 shrink-0 ${active ? "text-[hsl(var(--gold))]" : "text-muted-foreground"}`} />
                <div>
                  <div className={`text-sm font-medium ${active ? "text-foreground" : "text-foreground/85"}`}>
                    {opt.label}
                  </div>
                  <div className="text-[11px] text-muted-foreground mt-0.5">{opt.caption}</div>
                </div>
              </button>
            );
          })}
        </div>
      </section>

      {/* Sticky save bar */}
      <div className="mt-10 flex items-center justify-between gap-4">
        <span className="text-[11px] text-muted-foreground italic">
          {dirty ? "Unsaved changes." : "All changes saved."}
        </span>
        <button
          type="button"
          onClick={onSave}
          disabled={!dirty || saving}
          className="inline-flex items-center gap-2 px-6 h-10 bg-[hsl(var(--gold))] text-background text-[10px] uppercase tracking-[0.32em] disabled:opacity-40 disabled:cursor-not-allowed hover:opacity-90 transition-opacity"
          data-testid="profile-save-btn"
        >
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
          {saving ? "Saving…" : "Save changes"}
        </button>
      </div>

      {/* Crop dialog — appears between "picked a file" and "uploaded".
          Cancelling discards the pick. Confirming hands us a square JPEG
          blob which we ship to the same /admin/uploads endpoint. */}
      {pendingFile ? (
        <AvatarCropDialog
          file={pendingFile}
          onCancel={() => setPendingFile(null)}
          onConfirm={(blob, filename) => uploadCroppedBlob(blob, filename)}
        />
      ) : null}

      {/* Change-email modal — re-auth-gated self-service rotation. */}
      {showChangeEmail ? (
        <ChangeEmailDialog
          currentEmail={user.email}
          onClose={() => setShowChangeEmail(false)}
          onSuccess={(refreshedUser, newToken) => {
            // setSession(token, userData) — positional, per AuthContext.jsx
            setSession(newToken, refreshedUser);
            setShowChangeEmail(false);
            toast.success("Email updated.");
          }}
        />
      ) : null}
    </div>
  );
}

/* ChangeEmailDialog — minimal re-auth modal. Two inputs (new email +
   current password) plus a Cancel/Confirm pair. Wires up to the backend
   POST /api/auth/me/change-email endpoint which returns a fresh JWT
   carrying the new email claim (the parent then reseeds AuthContext).

   Why a separate component: keeps the parent AdminProfile clean and lets
   us reuse the same dialog from the StudioOnboardingWizard later. */
function ChangeEmailDialog({ currentEmail, onClose, onSuccess }) {
  const [newEmail, setNewEmail] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  async function submit(e) {
    e.preventDefault();
    setError("");
    const trimmed = newEmail.trim().toLowerCase();
    if (!trimmed) return setError("Enter a new email address.");
    if (trimmed === (currentEmail || "").toLowerCase()) {
      return setError("That's already your email on file.");
    }
    if (!currentPassword) return setError("Enter your current password to confirm.");

    setSubmitting(true);
    try {
      const r = await api.post("/auth/me/change-email", {
        new_email: trimmed,
        current_password: currentPassword,
      });
      onSuccess(r.data.user, r.data.token);
    } catch (err) {
      setError(formatApiErrorDetail(err) || "Couldn't change the email. Try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 bg-background/85 backdrop-blur-sm flex items-center justify-center px-5"
      data-testid="change-email-dialog"
      onClick={(e) => { if (e.target === e.currentTarget && !submitting) onClose(); }}
    >
      <form
        onSubmit={submit}
        className="w-full max-w-md bg-card border border-border rounded-sm p-7 shadow-2xl"
      >
        <div className="text-[10px] uppercase tracking-[0.32em] text-[hsl(var(--gold))]">
          Change admin email
        </div>
        <h2
          className="mt-3 leading-tight font-serif font-light"
          style={{ fontSize: "clamp(1.5rem, 3vw, 1.875rem)" }}
        >
          Where should we send your password resets?
        </h2>
        <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
          The new address is what you&apos;ll sign in with going forward. Your password stays the same — you&apos;ll just enter it here once to confirm it&apos;s you.
        </p>

        <div className="mt-6 space-y-4">
          <label className="block">
            <span className="block text-[10px] uppercase tracking-[0.32em] text-muted-foreground mb-2">
              New email
            </span>
            <input
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              autoFocus
              autoComplete="email"
              placeholder="your-real-inbox@example.com"
              className="w-full h-10 px-3 bg-background border border-border rounded-sm text-sm text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:border-[hsl(var(--gold))] transition-colors"
              data-testid="change-email-new-input"
              required
            />
            <span className="block text-[11px] text-muted-foreground/70 mt-1.5">
              Currently signed in as <span className="font-mono">{currentEmail}</span>
            </span>
          </label>

          <label className="block">
            <span className="block text-[10px] uppercase tracking-[0.32em] text-muted-foreground mb-2">
              Confirm with your current password
            </span>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                autoComplete="current-password"
                className="w-full h-10 px-3 pr-12 bg-background border border-border rounded-sm text-sm text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:border-[hsl(var(--gold))] transition-colors"
                data-testid="change-email-password-input"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="absolute inset-y-0 right-2 text-[10px] uppercase tracking-[0.32em] text-muted-foreground hover:text-[hsl(var(--gold))] transition-colors px-2"
                data-testid="change-email-toggle-password"
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? "hide" : "show"}
              </button>
            </div>
          </label>
        </div>

        {error ? (
          <div
            className="mt-5 px-3 py-2.5 border border-destructive/40 bg-destructive/10 text-destructive text-sm rounded-sm"
            data-testid="change-email-error"
            role="alert"
          >
            {error}
          </div>
        ) : null}

        <div className="mt-7 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            data-testid="change-email-cancel"
            className="h-10 px-5 text-[10px] uppercase tracking-[0.32em] text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            data-testid="change-email-confirm"
            className="h-10 px-6 bg-[hsl(var(--gold))] text-background text-[10px] uppercase tracking-[0.32em] hover:bg-[hsl(var(--gold))]/90 transition-colors disabled:opacity-50 inline-flex items-center gap-2"
          >
            {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
            {submitting ? "Saving…" : "Confirm change"}
          </button>
        </div>
      </form>
    </div>
  );
}
